// src/pages/admin/GroupsPage.js
import React, { useState, useEffect, useCallback } from "react";
import authService from "../../services/authService";
import { useToast } from "../../components/common/Toast";
import { IconLayers, IconPlus, IconTrash, IconX, IconEdit, IconShield } from "../../components/common/Icons";

export default function GroupsPage() {
  const toast = useToast();
  const [groups, setGroups] = useState([]);
  const [allPerms, setAllPerms] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreate, setShowCreate] = useState(false);
  const [form, setForm] = useState({ name: "", level: 2, description: "", permissions: [] });
  const [editingId, setEditingId] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    const [g, p] = await Promise.all([authService.getGroups(), authService.getPermissions()]);
    if (g.success) setGroups(g.data);
    if (p.success) setAllPerms(p.data);
    setLoading(false);
  }, []);

  useEffect(() => { load(); }, [load]);

  const resetForm = () => {
    setForm({ name: "", level: 2, description: "", permissions: [] });
    setEditingId(null);
    setShowCreate(false);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    let res;
    if (editingId) {
      res = await authService.updateGroup(editingId, form);
    } else {
      res = await authService.createGroup(form);
    }

    if (res.success) {
      toast(editingId ? "Group updated" : "Group created", "success");
      resetForm();
      load();
    } else {
      const msg = typeof res.error === "object" ? Object.values(res.error).flat().join(", ") : String(res.error);
      toast(msg, "error");
    }
  };

  const handleEdit = (group) => {
    setForm({
      name: group.name,
      level: group.level,
      description: group.description || "",
      permissions: group.permissions || [],
    });
    setEditingId(group.id);
    setShowCreate(true);
  };

  const handleDelete = async (group) => {
    if (!window.confirm(`Delete group "${group.name}"?`)) return;
    const res = await authService.deleteGroup(group.id);
    if (res.success) {
      toast("Group deleted", "success");
      load();
    } else {
      toast("Failed to delete", "error");
    }
  };

  const togglePerm = (id) => {
    setForm((f) => ({
      ...f,
      permissions: f.permissions.includes(id) ? f.permissions.filter((x) => x !== id) : [...f.permissions, id],
    }));
  };

  const inputCls = "w-full px-3 py-2.5 bg-onyx-950 border border-onyx-700/50 rounded-lg text-onyx-100 font-body text-sm placeholder-onyx-600 focus:outline-none focus:border-tiger-500/50 transition-all";
  const labelCls = "block text-xs font-display text-onyx-500 tracking-wider uppercase mb-1.5";

  return (
    <div className="max-w-7xl mx-auto px-4 py-10">
      <div className="flex items-center justify-between mb-8">
        <div className="flex items-center gap-3">
          <IconLayers size={22} className="text-tiger-500" />
          <h1 className="font-display text-2xl font-bold text-onyx-100 tracking-wider">GROUPS</h1>
          <span className="text-sm font-mono text-onyx-500">{groups.length} total</span>
        </div>
        <button
          onClick={() => showCreate ? resetForm() : setShowCreate(true)}
          className="flex items-center gap-2 px-4 py-2.5 rounded-lg font-display font-semibold text-sm tracking-wider
            bg-gradient-to-r from-tiger-600 to-tiger-500 text-white hover:from-tiger-500 hover:to-tiger-400 transition-all"
        >
          {showCreate ? <IconX size={16} /> : <IconPlus size={16} />}
          {showCreate ? "CANCEL" : "NEW GROUP"}
        </button>
      </div>

      {/* Create / Edit form */}
      {showCreate && (
        <div className="mb-8 p-6 rounded-xl border border-tiger-500/20 bg-onyx-900/60 animate-slide-down">
          <h2 className="font-display text-sm font-semibold text-tiger-400 tracking-widest mb-4">
            {editingId ? "EDIT GROUP" : "CREATE GROUP"}
          </h2>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div>
                <label className={labelCls}>Group Name</label>
                <input className={inputCls} value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })}
                  placeholder="e.g. Coach" required />
              </div>
              <div>
                <label className={labelCls}>Level</label>
                <input className={inputCls} type="number" min={1} max={99} value={form.level}
                  onChange={(e) => setForm({ ...form, level: parseInt(e.target.value) || 2 })} />
              </div>
              <div>
                <label className={labelCls}>Description</label>
                <input className={inputCls} value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })}
                  placeholder="Optional" />
              </div>
            </div>

            {/* Permission picker */}
            <div>
              <label className={labelCls}>Permissions</label>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-1.5 max-h-52 overflow-y-auto p-3 rounded-lg bg-onyx-950 border border-onyx-700/30">
                {allPerms.map((p) => {
                  const fits = p.min_level <= form.level;
                  return (
                    <label
                      key={p.id}
                      className={`flex items-center gap-2 px-2.5 py-1.5 rounded-md transition-colors cursor-pointer
                        ${fits ? "hover:bg-onyx-800/50" : "opacity-30 cursor-not-allowed"}`}
                    >
                      <input
                        type="checkbox"
                        checked={form.permissions.includes(p.id)}
                        disabled={!fits}
                        onChange={() => fits && togglePerm(p.id)}
                        className="rounded border-onyx-600 text-tiger-500 focus:ring-tiger-500 bg-onyx-900"
                      />
                      <span className="text-xs font-mono text-onyx-300 truncate">{p.codename}</span>
                      <span className="text-xs text-onyx-600 ml-auto flex-shrink-0">L{p.min_level}</span>
                    </label>
                  );
                })}
              </div>
              <p className="text-xs text-onyx-600 mt-1">
                Only permissions with min_level ≤ group level ({form.level}) can be included
              </p>
            </div>

            <button type="submit" className="px-6 py-2.5 rounded-lg font-display font-semibold text-sm tracking-wider
              bg-gradient-to-r from-tiger-600 to-tiger-500 text-white hover:from-tiger-500 hover:to-tiger-400 transition-all">
              {editingId ? "UPDATE GROUP" : "CREATE GROUP"}
            </button>
          </form>
        </div>
      )}

      {/* Groups list */}
      {loading ? (
        <div className="text-center py-20">
          <div className="inline-block w-8 h-8 border-2 border-tiger-500/30 border-t-tiger-500 rounded-full animate-spin" />
        </div>
      ) : (
        <div className="space-y-4">
          {groups.map((g) => (
            <div key={g.id} className="rounded-xl bg-onyx-900/50 border border-onyx-800/30 hover:border-onyx-700/40 transition-all overflow-hidden">
              <div className="flex items-center justify-between px-5 py-4">
                <div className="flex items-center gap-4">
                  <div className="p-2 rounded-lg bg-tiger-500/10">
                    <IconLayers size={18} className="text-tiger-500" />
                  </div>
                  <div>
                    <h3 className="font-display font-semibold text-onyx-100 tracking-wide">{g.name}</h3>
                    <p className="text-xs text-onyx-500 font-body mt-0.5">Level {g.level} {g.description && `· ${g.description}`}</p>
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  <button
                    onClick={() => handleEdit(g)}
                    className="p-2 text-onyx-500 hover:text-tiger-400 hover:bg-tiger-500/10 rounded-lg transition-colors"
                    title="Edit"
                  >
                    <IconEdit size={16} />
                  </button>
                  <button
                    onClick={() => handleDelete(g)}
                    className="p-2 text-onyx-500 hover:text-red-400 hover:bg-red-500/10 rounded-lg transition-colors"
                    title="Delete"
                  >
                    <IconTrash size={16} />
                  </button>
                </div>
              </div>
              {/* Permissions in group */}
              {g.permission_details && g.permission_details.length > 0 && (
                <div className="px-5 pb-4">
                  <div className="flex items-center gap-2 mb-2">
                    <IconShield size={12} className="text-onyx-600" />
                    <span className="text-xs font-display text-onyx-600 tracking-wider">PERMISSIONS</span>
                  </div>
                  <div className="flex flex-wrap gap-1.5">
                    {g.permission_details.map((p) => (
                      <span key={p.id} className="px-2 py-0.5 text-xs font-mono bg-onyx-800/40 text-onyx-400 rounded border border-onyx-700/20">
                        {p.codename}
                      </span>
                    ))}
                  </div>
                </div>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
